import React, { useEffect, useRef, useState } from "react";

function simpleBotReply(text) {
  const t = String(text || "").toLowerCase();
  if (t.includes("delivery"))
    return "Delivery is available. You can choose self-collect at checkout too.";
  if (t.includes("promo"))
    return "Promo codes can be applied in Checkout. Try the sample codes provided.";
  if (t.includes("refund"))
    return "For refunds, contact the seller via the listing details.";
  if (t.includes("seller"))
    return "Sellers can add items under Profile → Seller tools.";
  return "I can help with items, categories, delivery, and promo codes.";
}

export default function ChatModal({ visible, onClose }) {
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([
    {
      from: "bot",
      text: "Hi! Ask me about delivery, promo codes, refunds, or how to use SecondAid.",
    },
  ]);

  const endRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll + focus input when opened / new message
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
      inputRef.current?.focus?.();
    }, 50);
    return () => clearTimeout(t);
  }, [visible, chat.length]);

  // Close when clicking outside OR pressing Esc
  useEffect(() => {
    if (!visible) return;

    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
    }

    function onMouseDown(e) {
      const clickedFab = e.target?.closest?.(".chatFab");
      if (clickedFab) return;

      const el = panelRef.current;
      if (!el) return;

      if (!el.contains(e.target)) onClose?.();
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [visible, onClose]);

  function pushUserText(text) {
    const clean = String(text || "").trim();
    if (!clean) return;
    setChat((prev) => [
      ...prev,
      { from: "user", text: clean },
      { from: "bot", text: simpleBotReply(clean) },
    ]);
  }

  function send() {
    const clean = String(message || "").trim();
    if (!clean) return;
    pushUserText(clean);
    setMessage("");
  }

  if (!visible) return null;

  return (
    <div className="chatDock" aria-live="polite">
      <div ref={panelRef} className="chatPanel" role="dialog" aria-label="Chat">
        <div className="chatPanelHeader">
          <div className="chatAgent">
            <div className="chatAvatar">SA</div>
            <div className="chatAgentText">
              <div className="chatTitle">SecondAid Support</div>
              <div className="chatStatus">
                <span className="chatDot" />
                Online
              </div>
            </div>
          </div>

          <button
            className="chatCloseBtn"
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="chatPanelBody">
          {chat.map((c, idx) => (
            <div
              key={idx}
              className={c.from === "bot" ? "chatRow bot" : "chatRow user"}
            >
              <div>
                <div className="chatMeta">
                  {c.from === "bot" ? "Bot" : "You"}
                </div>
                <div
                  className={
                    c.from === "bot" ? "chatBubble bot" : "chatBubble user"
                  }
                >
                  {c.text}
                </div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="chatPanelFooter">
          <div className="chatChipsRow">
            <button
              className="chatChip"
              type="button"
              onClick={() => pushUserText("Delivery")}
            >
              Delivery
            </button>
            <button
              className="chatChip"
              type="button"
              onClick={() => pushUserText("Promo codes")}
            >
              Promo codes
            </button>
            <button
              className="chatChip"
              type="button"
              onClick={() => pushUserText("Refund")}
            >
              Refunds
            </button>
          </div>

          <div className="chatComposer">
            <input
              ref={inputRef}
              className="chatInput"
              value={message}
              placeholder="Type a message..."
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <button
              className="chatSendBtn"
              type="button"
              onClick={send}
              disabled={!String(message || "").trim()}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
